import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Table, Badge, Button, Spinner, Alert, Modal, Row, Col, Form, InputGroup } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { GoogleMap, MarkerF, Polyline } from '@react-google-maps/api';
import useAuthStore from '../../store/useAuthStore';
import { getTrips, requesterCancelTrip } from '../api';
import apiClient from '../../api';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

// Modal de Seguimiento en Vivo
const LiveTrackingModal = ({ show, onHide, tripId }) => {
    const [trip, setTrip] = useState(null);
    const [positions, setPositions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchTrackingData = useCallback(async () => {
        try {
            const [tripRes, posRes] = await Promise.all([
                apiClient.get(`/transport/trips/${tripId}`),
                apiClient.get(`/transport/trips/${tripId}/positions`)
            ]);
            setTrip(tripRes.data);
            setPositions(posRes.data || []);
            setError(null);
        } catch (err) {
            console.error("Error al obtener seguimiento:", err);
            setError("No se pudo actualizar el GPS. Reintentando...");
        } finally {
            setLoading(false);
        }
    }, [tripId]);

    useEffect(() => {
        if (!show || !tripId) return;

        setLoading(true);
        fetchTrackingData();

        // Polling cada 15 segundos
        const interval = setInterval(fetchTrackingData, 15000);
        return () => clearInterval(interval);
    }, [show, tripId, fetchTrackingData]);

    if (!show) return null;

    const mapContainerStyle = {
        width: '100%',
        height: '400px',
        borderRadius: '12px'
    };

    const getStatusLabel = (status) => {
        const labels = {
            'pending':   'Buscando Conductor',
            'assigned':  'Conductor Asignado - Esperando Confirmación',
            'confirmed': 'Conductor Confirmado',
            'en_route':  'Conductor en Camino',
            'arrived':   'El Conductor ha Llegado',
            'on_board':  'En Viaje',
            'completed': 'Viaje Finalizado',
            'cancelled': 'Cancelado',
            'no_show':   'No Show'
        };
        return labels[status] || status;
    };

    const pickupLoc = trip?.pickup_lat && trip?.pickup_lng ? { lat: Number(trip.pickup_lat), lng: Number(trip.pickup_lng) } : null;
    const dropoffLoc = trip?.dropoff_lat && trip?.dropoff_lng ? { lat: Number(trip.dropoff_lat), lng: Number(trip.dropoff_lng) } : null;
    const driverLoc = positions.length > 0 && trip?.status !== 'confirmed' ? { lat: Number(positions[positions.length - 1].lat), lng: Number(positions[positions.length - 1].lng) } : null;

    const defaultCenter = driverLoc || pickupLoc || { lat: -33.4489, lng: -70.6693 };

    const getWhatsAppLink = (phone) => {
        if (!phone) return '#';
        const cleaned = phone.replace(/\D/g, '');
        const withPrefix = cleaned.startsWith('56') ? cleaned : `56${cleaned}`;
        return `https://wa.me/${withPrefix}?text=Hola,%20buenas.%20Soy%20el%20pasajero%20de%20tu%20servicio%20de%20transporte.`;
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fw-bold text-primary d-flex align-items-center gap-2">
                    <span>📡 Seguimiento en Vivo</span>
                    {trip && <span className="badge bg-light text-primary border">Servicio #{trip.id}</span>}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-3">
                {loading && !trip ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" className="mb-2" />
                        <div className="text-muted">Cargando localización...</div>
                    </div>
                ) : (
                    <Row className="g-4">
                        <Col lg={7}>
                            <div className="position-relative" style={{ height: '400px', backgroundColor: '#e9ecef', borderRadius: '12px', overflow: 'hidden' }}>
                                {window.google ? (
                                    <GoogleMap
                                        mapContainerStyle={mapContainerStyle}
                                        center={defaultCenter}
                                        zoom={14}
                                        options={{
                                            streetViewControl: false,
                                            mapTypeControl: false,
                                            fullscreenControl: false
                                        }}
                                    >
                                        {pickupLoc && (
                                            <MarkerF
                                                position={pickupLoc}
                                                label={{ text: "🟢 Origen", color: "#198754", fontWeight: "bold", fontSize: "11px" }}
                                            />
                                        )}
                                        {dropoffLoc && (
                                            <MarkerF
                                                position={dropoffLoc}
                                                label={{ text: "🔴 Destino", color: "#dc3545", fontWeight: "bold", fontSize: "11px" }}
                                            />
                                        )}
                                        {driverLoc && (
                                            <MarkerF
                                                position={driverLoc}
                                                icon={{
                                                    path: window.google?.maps?.SymbolPath?.CIRCLE,
                                                    scale: 8,
                                                    fillColor: '#0d6efd',
                                                    fillOpacity: 1,
                                                    strokeColor: '#ffffff',
                                                    strokeWeight: 2.5
                                                }}
                                                label={{ text: "🚗 Conductor", color: "#0d6efd", fontWeight: "bold", fontSize: "12px" }}
                                            />
                                        )}
                                        {positions.length > 0 && trip?.status !== 'confirmed' && (
                                            <Polyline
                                                path={positions.map(p => ({ lat: Number(p.lat), lng: Number(p.lng) }))}
                                                options={{
                                                    strokeColor: '#0d6efd',
                                                    strokeOpacity: 0.8,
                                                    strokeWeight: 4
                                                }}
                                            />
                                        )}
                                    </GoogleMap>
                                ) : (
                                    <div className="d-flex justify-content-center align-items-center h-100 text-muted">
                                        Inicializando mapas...
                                    </div>
                                )}
                            </div>
                        </Col>
                        
                        <Col lg={5}>
                            <div className="d-flex flex-column h-100 justify-content-between">
                                <div>
                                    <h5 className="fw-bold mb-1">{trip?.passenger_name}</h5>
                                    <div className="small text-muted mb-3">
                                        🟢 Origen: {trip?.pickup_address} <br/> 
                                        🔴 Destino: {trip?.dropoff_address}
                                    </div>

                                    <div className="p-3 bg-light rounded-3 mb-3">
                                        <div className="text-muted small mb-1">Estado del Servicio</div>
                                        <div className="fw-bold text-primary h6 mb-0 d-flex align-items-center gap-2">
                                            <span className="position-relative d-inline-flex">
                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                                                <span className="relative inline-flex rounded-full h-3 w-3 bg-primary"></span>
                                            </span>
                                            {getStatusLabel(trip?.status)}
                                        </div>
                                    </div>

                                    {/* ============================================
                                        LÓGICA DE VISIBILIDAD DEL CONDUCTOR
                                        - assigned:  locutora asignó, conductor aún no confirma
                                        - confirmed: conductor aceptó → mostrar info
                                        - en_route+: en camino → info + GPS
                                    ============================================ */}
                                    {trip?.status === 'assigned' ? (
                                        // Asignado por la locutora, pendiente de aceptación del conductor
                                        <div className="text-center p-3 border border-warning border-opacity-50 rounded-3 bg-warning bg-opacity-10 small">
                                            <div className="fw-bold text-warning mb-1">🕐 Conductor Asignado</div>
                                            <span className="text-muted">Esperando confirmación del conductor...</span>
                                        </div>
                                    ) : trip?.driver && ['confirmed', 'en_route', 'arrived', 'on_board'].includes(trip?.status) ? (
                                        // Conductor confirmado o en camino → mostrar info completa
                                        <div className="border rounded-3 p-3 bg-white shadow-sm">
                                            <div className="d-flex align-items-center gap-3 mb-3">
                                                <div className="position-relative" style={{ width: '42px', height: '42px' }}>
                                                    {/* Fallback de Inicial (siempre de fondo) */}
                                                    <div 
                                                        className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center fw-bold text-uppercase border border-2 border-white shadow-sm" 
                                                        style={{ width: '42px', height: '42px', fontSize: '15px' }}
                                                    >
                                                        {trip.driver.full_name?.charAt(0)}
                                                    </div>
                                                    {/* Foto de Perfil (se posiciona encima del fallback) */}
                                                    <img 
                                                        src={`${apiClient.defaults.baseURL || ''}/transport/drivers/${trip.driver.id}/profile-image`} 
                                                        alt={trip.driver.full_name}
                                                        className="rounded-circle border border-2 border-white shadow-sm position-absolute top-0 start-0 bg-white"
                                                        style={{ width: '42px', height: '42px', objectFit: 'cover' }}
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                        }}
                                                    />
                                                </div>
                                                <div>
                                                    <div className="fw-bold small">{trip.driver.full_name}</div>
                                                    <div className="text-muted" style={{ fontSize: '11px' }}>👨‍✈️ Tu conductor confirmado</div>
                                                </div>
                                            </div>

                                            <div className="bg-light p-2 rounded small mb-3" style={{ fontSize: '12px' }}>
                                                <strong>Vehículo:</strong> {
                                                    [trip.vehicle?.make, trip.vehicle?.model].filter(Boolean).join(' ') || 
                                                    [trip.driver?.vehicle_make, trip.driver?.vehicle_model].filter(Boolean).join(' ') || 
                                                    trip.requested_vehicle_type?.name || 'Vehículo Asignado'
                                                } <br/>
                                                <strong>Patente:</strong> <span className="badge bg-dark text-white px-2 py-0.5">
                                                    {trip.vehicle?.plate || trip.driver?.vehicle_plate || trip.driver?.vehicle?.plate || 'S/P'}
                                                </span>
                                            </div>

                                            {trip.driver.phone && (
                                                <div className="d-grid gap-1">
                                                    <a 
                                                        href={getWhatsAppLink(trip.driver.phone)} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer" 
                                                        className="btn btn-success btn-sm d-flex align-items-center justify-content-center gap-2"
                                                    >
                                                        💬 WhatsApp
                                                    </a>
                                                    <a 
                                                        href={`tel:${trip.driver.phone}`} 
                                                        className="btn btn-outline-secondary btn-sm"
                                                    >
                                                        📞 Llamar Conductor
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        // pending o sin conductor
                                        <div className="text-center p-3 border border-dashed rounded-3 text-muted small">
                                            🕒 Asignando conductor de la flota...
                                        </div>
                                    )}
                                </div>
                                
                                {error && <div className="text-danger small mt-2">⚠️ {error}</div>}
                            </div>
                        </Col>
                    </Row>
                )}
            </Modal.Body>
        </Modal>
    );
};

const getMonthLabel = (dateStr) => {
    const date = new Date(dateStr);
    const label = date.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' });
    return label.charAt(0).toUpperCase() + label.slice(1);
};

const ACTIVE_STATUSES = ['pending', 'assigned', 'confirmed', 'en_route', 'arrived', 'on_board'];

const getFirstDayOfCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
};

const EMPTY_FILTERS = {
  passengerSearch: '',
  requesterSearch: '',
  areaIds: [],
  costCenterIds: [],
  startDate: getFirstDayOfCurrentMonth(), // por defecto: 1° del mes actual
  endDate: '',
};

const MyTripsPage = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- FILTROS ---
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [areaSearch, setAreaSearch] = useState('');
  const [ccSearch, setCcSearch] = useState('');
  const [showAreaDropdown, setShowAreaDropdown] = useState(false);
  const [showCcDropdown, setShowCcDropdown] = useState(false);

  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [selectedTripId, setSelectedTripId] = useState(null);

  const [expandedMonths, setExpandedMonths] = useState({});

  const toggleMonth = (monthLabel) => {
      setExpandedMonths(prev => ({
          ...prev,
          [monthLabel]: !prev[monthLabel]
      }));
  };

  // --- CANCELACIÓN Y EDICIÓN POR SOLICITANTE ---
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [tripToCancel, setTripToCancel] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  const canEditOrCancelTrip = (trip) => {
    const statusLower = (trip.status || '').toLowerCase();
    // Solo permitir editar/cancelar si el estado es 'pending' (Buscando conductor) o 'assigned' (Esp. Confirmación)
    // Si ya está 'confirmed' (Confirmado por conductor), no se puede editar ni cancelar desde el portal.
    if (!['pending', 'assigned'].includes(statusLower)) return false;

    const scheduled = new Date(trip.scheduled_time);
    const now = new Date();
    const diffMs = scheduled - now;
    const diffHours = diffMs / (1000 * 60 * 60);
    return diffHours > 24;
  };

  const handleShowCancelModal = (trip) => {
    setTripToCancel(trip);
    setShowCancelModal(true);
  };

  const handleCloseCancelModal = () => {
    setTripToCancel(null);
    setShowCancelModal(false);
  };

  const handleConfirmCancel = async () => {
    if (!tripToCancel) return;
    setCancelLoading(true);
    try {
      await requesterCancelTrip(tripToCancel.id);
      toast.success(`Viaje #${tripToCancel.id} cancelado correctamente.`);
      handleCloseCancelModal();
      fetchMyTrips();
    } catch (err) {
      console.error("Error al cancelar viaje:", err);
      const errMsg = err.response?.data?.detail || "No se pudo cancelar el viaje.";
      toast.error(errMsg);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleEditTrip = (trip) => {
    navigate('/portal/new-trip', { state: { editTrip: trip } });
  };

  // --- EXPORTACIÓN A EXCEL ---
  const handleExportExcel = () => {
    if (filteredTrips.length === 0) {
      toast.warning("No hay datos para exportar.");
      return;
    }

    const dataToExport = filteredTrips.map(t => {
      const scheduledDate = new Date(t.scheduled_time);
      const formattedDate = scheduledDate.toLocaleDateString('es-CL');
      const formattedTime = scheduledDate.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });

      const labels = {
        'pending':   'Buscando Conductor',
        'assigned':  'Esperando Confirmación del Conductor',
        'confirmed': 'Confirmado',
        'en_route':  'Conductor en Camino',
        'arrived':   'El Conductor ha Llegado',
        'on_board':  'En Viaje',
        'completed': 'Finalizado',
        'cancelled': 'Cancelado',
        'no_show':   'No Show'
      };
      const statusText = labels[t.status] || t.status;

      const baseRow = {
        "ID Reserva": t.id,
        "Fecha": formattedDate,
        "Hora": formattedTime,
        "Origen": t.pickup_address,
        "Destino": t.dropoff_address,
        "Vehículo Solicitado": t.requested_vehicle_type?.name || 'Estándar',
        "Valor Empresa (CLP)": Math.round(t.final_price !== null && t.final_price !== undefined ? t.final_price : (t.estimated_price || 0)),
        "Estado": statusText
      };

      if (user.role === 'requester') {
        return {
          ...baseRow,
          "Área": t.area?.name || '---',
          "Centro Costo": t.cost_center ? `${t.cost_center.code || ''} ${t.cost_center.name || ''}`.trim() || '---' : '---',
          "Pasajero Principal": t.passenger_name || '---',
          "Teléfono Pasajero": t.passenger_phone || '---',
          "Acompañantes": t.additional_passengers ? t.additional_passengers.map(p => `${p.full_name} (x${p.pax_count || 1})`).join(', ') : 'Ninguno',
          "N° Vuelo": t.flight_number || '---'
        };
      }
      return baseRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Mis Viajes");

    const fileName = `mis_viajes_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    toast.success("Excel descargado con éxito.");
  };

  const fetchMyTrips = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      let myTrips = [];

      if (user.role === 'passenger' && user.linked_passenger_id) {
          const profileRes = await apiClient.get(`/transport/passengers/${user.linked_passenger_id}`);
          const myProfile = profileRes.data;
          
          const allClientTrips = await getTrips({ client_id: myProfile.client_id, limit: 1000 });
          
          myTrips = allClientTrips.filter(t => 
              t.passenger_name === myProfile.full_name || 
              (t.passenger_phone && myProfile.phone && t.passenger_phone === myProfile.phone)
          );

      } else if (user.role === 'requester' && user.linked_requester_id) {
          const profileRes = await apiClient.get(`/transport/requesters/${user.linked_requester_id}`);
          const myProfile = profileRes.data;
          
          const allClientTrips = await getTrips({ client_id: myProfile.client_id, limit: 1000 });
          
          // Requerimiento Alejandro: Los solicitantes de empresas B2B ven TODAS las reservas.
          // EXCEPCIÓN PARTICULARES: Si es del cliente "PARTICULARES SIN CONVENIO", solo ve SUS propias reservas.
          const isParticularClient = myProfile.client?.segmentation === "Particulares" || 
                                     myProfile.client?.name?.toUpperCase().includes("PARTICULARES");

          if (isParticularClient) {
              const myReqIdStr = String(myProfile.id || user.linked_requester_id || '');
              const myEmailLower = (myProfile.email || user.email || '').toLowerCase();
              const myNameLower = (myProfile.full_name || user.full_name || '').toLowerCase();

              myTrips = allClientTrips.filter(t => {
                  const tReqIdStr = String(t.requester_id || t.requester?.id || '');
                  const tReqEmailLower = (t.requester?.email || '').toLowerCase();
                  
                  const isMyRequest = myReqIdStr && tReqIdStr && tReqIdStr === myReqIdStr;
                  const isMyEmailReq = myEmailLower && tReqEmailLower && tReqEmailLower === myEmailLower;
                  const isMyPassengerName = myNameLower && t.passenger_name && t.passenger_name.toLowerCase() === myNameLower;
                  const isMyPassengerPhone = myProfile.phone && t.passenger_phone && t.passenger_phone === myProfile.phone;

                  return isMyRequest || isMyEmailReq || isMyPassengerName || isMyPassengerPhone;
              });
          } else {
              myTrips = allClientTrips;
          }
      } else if (user.role === 'requester' && !user.linked_requester_id) {
          const allTrips = await getTrips({ limit: 1000 });
          myTrips = allTrips;
      }

      myTrips.sort((a, b) => new Date(b.scheduled_time) - new Date(a.scheduled_time));
      setTrips(myTrips);

    } catch (err) {
      console.error("Error cargando viajes:", err);
      if (!silent) setError("No se pudieron cargar tus viajes.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [user]);

  // Carga inicial
  useEffect(() => {
    if (user) fetchMyTrips();
  }, [user, fetchMyTrips]);

  // Polling: cada 30s refresca silenciosamente si hay viajes activos
  useEffect(() => {
    const hasActiveTrips = trips.some(t => ACTIVE_STATUSES.includes(t.status));
    if (!hasActiveTrips) return;
    const interval = setInterval(() => fetchMyTrips(true), 30000);
    return () => clearInterval(interval);
  }, [trips, fetchMyTrips]);

  // --- OPCIONES DINÁMICAS (extraídas de los viajes cargados, sin llamada extra al servidor) ---
  const areaOptions = useMemo(() => {
    const map = new Map();
    trips.forEach(t => { if (t.area) map.set(t.area.id, t.area); });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [trips]);

  const costCenterOptions = useMemo(() => {
    const map = new Map();
    trips.forEach(t => { if (t.cost_center) map.set(t.cost_center.id, t.cost_center); });
    return [...map.values()].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [trips]);

  const requesterOptions = useMemo(() => {
    const map = new Map();
    trips.forEach(t => {
      const rName = t.external_solicitante || t.requester?.full_name;
      if (rName && rName.trim()) {
        map.set(rName.trim().toLowerCase(), rName.trim());
      }
    });
    return [...map.values()].sort((a, b) => a.localeCompare(b));
  }, [trips]);

  // --- VIAJES FILTRADOS (100% client-side, sin llamadas extras) ---
  const filteredTrips = useMemo(() => {
    return trips.filter(t => {
      const { passengerSearch, requesterSearch, areaIds, costCenterIds, startDate, endDate } = filters;
      if (passengerSearch && !t.passenger_name?.toLowerCase().includes(passengerSearch.toLowerCase())) return false;
      
      if (requesterSearch) {
        const rName = (t.external_solicitante || t.requester?.full_name || '').toLowerCase();
        if (!rName.includes(requesterSearch.toLowerCase())) return false;
      }

      if (areaIds.length > 0 && !areaIds.includes(t.area?.id)) return false;
      if (costCenterIds.length > 0 && !costCenterIds.includes(t.cost_center?.id)) return false;
      if (startDate && new Date(t.scheduled_time) < new Date(startDate + 'T00:00:00')) return false;
      if (endDate && new Date(t.scheduled_time) > new Date(endDate + 'T23:59:59')) return false;
      return true;
    });
  }, [trips, filters]);

  // --- TARJETAS DE RESUMEN (derivadas del filtro activo) ---
  const summaryTotalViajes = filteredTrips.length;
  const summaryTotalMonto = filteredTrips.reduce((acc, t) => {
    return acc + (t.final_price ?? t.estimated_price ?? 0);
  }, 0);

  const hasActiveFilters = Object.entries(filters).some(([k, v]) =>
    Array.isArray(v) ? v.length > 0 : v !== ''
  );

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setAreaSearch('');
    setCcSearch('');
  };

  const toggleArea = (id) => {
    setFilters(prev => ({
      ...prev,
      areaIds: prev.areaIds.includes(id)
        ? prev.areaIds.filter(x => x !== id)
        : [...prev.areaIds, id]
    }));
  };

  const toggleCostCenter = (id) => {
    setFilters(prev => ({
      ...prev,
      costCenterIds: prev.costCenterIds.includes(id)
        ? prev.costCenterIds.filter(x => x !== id)
        : [...prev.costCenterIds, id]
    }));
  };

  // activeTrips: orden ASCENDENTE (el más próximo arriba)
  // groupedPastTrips: orden DESCENDENTE (el más reciente arriba)
  const activeTrips = useMemo(() => {
      return filteredTrips
          .filter(t => ACTIVE_STATUSES.includes(t.status))
          .sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time));
  }, [filteredTrips]);

  const groupedPastTrips = useMemo(() => {
      const past = filteredTrips.filter(t => !ACTIVE_STATUSES.includes(t.status));
      const groups = {};
      past.forEach(trip => {
          const monthLabel = getMonthLabel(trip.scheduled_time);
          if (!groups[monthLabel]) {
              groups[monthLabel] = {
                  label: monthLabel,
                  trips: [],
                  totalSale: 0
              };
          }
          groups[monthLabel].trips.push(trip);
          const price = trip.final_price !== null && trip.final_price !== undefined ? trip.final_price : (trip.estimated_price || 0);
          groups[monthLabel].totalSale += price;
      });
      return Object.values(groups);
  }, [filteredTrips]);

  const getStatusBadge = (status) => {
      const map = {
          'pending':   'warning',
          'assigned':  'warning',
          'confirmed': 'info',
          'en_route':  'primary',
          'arrived':   'primary',
          'on_board':  'success',
          'completed': 'success',
          'cancelled': 'danger',
          'no_show':   'dark'
      };
      const labels = {
          'pending':   'Buscando Conductor',
          'assigned':  'Esp. Confirmación',
          'confirmed': '✅ Confirmado',
          'en_route':  'En Camino',
          'arrived':   'Ha Llegado',
          'on_board':  'En Viaje',
          'completed': 'Finalizado',
          'cancelled': 'Cancelado',
          'no_show':   'No Show'
      };
      return <Badge bg={map[status] || 'secondary'}>{labels[status] || status}</Badge>;
  };

  if (loading) return <div className="text-center py-5"><Spinner animation="border" /></div>;

  return (
    <div className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>📅 Mis Viajes</h3>
        <div className="d-flex gap-2">
            {user.role === 'requester' && (
                <Button variant="outline-success" onClick={handleExportExcel}>
                    📊 Descargar Excel
                </Button>
            )}
            <Link to="/portal/new-trip">
                <Button variant="primary">Nuevo Viaje</Button>
            </Link>
        </div>
      </div>

      {/* ============================================================
          TARJETAS DE RESUMEN + FILTROS (solo para rol solicitante)
      ============================================================ */}
      {user.role === 'requester' && (
        <div className="mb-4">
          {/* Tarjetas de Resumen */}
          <Row className="g-3 mb-3">
            <Col xs={6} md={3}>
              <Card className="border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #0d6efd', borderRadius: '10px' }}>
                <Card.Body className="d-flex flex-column justify-content-center">
                  <div className="text-muted small mb-1">📋 Total Servicios</div>
                  <div className="fw-bold" style={{ fontSize: '2rem', lineHeight: 1 }}>{summaryTotalViajes}</div>
                  {hasActiveFilters && <div className="text-muted" style={{ fontSize: '10px' }}>filtrado</div>}
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={4}>
              <Card className="border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #198754', borderRadius: '10px' }}>
                <Card.Body className="d-flex flex-column justify-content-center">
                  <div className="text-muted small mb-1">💰 Monto Total CLP</div>
                  <div className="fw-bold text-success" style={{ fontSize: '1.6rem', lineHeight: 1 }}>
                    ${Math.round(summaryTotalMonto).toLocaleString('es-CL')}
                  </div>
                  {hasActiveFilters && <div className="text-muted" style={{ fontSize: '10px' }}>filtrado</div>}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Panel de Filtros */}
          <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
            <Card.Body className="py-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="fw-semibold text-secondary small">🔍 Filtros</span>
                {hasActiveFilters && (
                  <Button size="sm" variant="outline-secondary" onClick={resetFilters} style={{ fontSize: '12px' }}>
                    ✖ Restablecer filtros
                  </Button>
                )}
              </div>

              <Row className="g-2">
                {/* Pasajero */}
                <Col xs={12} sm={6} lg={3}>
                  <Form.Label className="small mb-1 text-muted">Pasajero</Form.Label>
                  <InputGroup size="sm">
                    <InputGroup.Text>👤</InputGroup.Text>
                    <Form.Control
                      size="sm"
                      placeholder="Buscar por nombre..."
                      value={filters.passengerSearch}
                      onChange={e => setFilters(prev => ({ ...prev, passengerSearch: e.target.value }))}
                    />
                    {filters.passengerSearch && (
                      <Button size="sm" variant="outline-secondary" onClick={() => setFilters(prev => ({ ...prev, passengerSearch: '' }))}>
                        ×
                      </Button>
                    )}
                  </InputGroup>
                </Col>

                {/* Solicitante con Buscador y atajo a Míos */}
                <Col xs={12} sm={6} lg={3}>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Form.Label className="small mb-0 text-muted">Solicitante</Form.Label>
                    {user?.full_name && (
                      <Button 
                        variant="link" 
                        className="p-0 border-0 text-decoration-none small text-primary" 
                        style={{ fontSize: '11px' }}
                        onClick={() => setFilters(prev => ({ ...prev, requesterSearch: user.full_name }))}
                      >
                        👤 Mis Servicios
                      </Button>
                    )}
                  </div>
                  <InputGroup size="sm">
                    <InputGroup.Text>👥</InputGroup.Text>
                    <Form.Control
                      size="sm"
                      placeholder="Buscar o seleccionar..."
                      list="requester-list-options"
                      value={filters.requesterSearch}
                      onChange={e => setFilters(prev => ({ ...prev, requesterSearch: e.target.value }))}
                    />
                    <datalist id="requester-list-options">
                      {requesterOptions.map((reqName, idx) => (
                        <option key={idx} value={reqName} />
                      ))}
                    </datalist>
                    {filters.requesterSearch && (
                      <Button size="sm" variant="outline-secondary" onClick={() => setFilters(prev => ({ ...prev, requesterSearch: '' }))}>
                        ×
                      </Button>
                    )}
                  </InputGroup>
                </Col>

                {/* Área - Dropdown con checkboxes */}
                <Col xs={12} sm={6} lg={3}>
                  <Form.Label className="small mb-1 text-muted">Área</Form.Label>
                  <div className="position-relative">
                    <Button
                      size="sm"
                      variant={filters.areaIds.length > 0 ? 'primary' : 'outline-secondary'}
                      className="w-100 text-start d-flex justify-content-between align-items-center"
                      onClick={() => { setShowAreaDropdown(p => !p); setShowCcDropdown(false); }}
                      style={{ fontSize: '13px' }}
                    >
                      <span>
                        {filters.areaIds.length === 0
                          ? '🏢 Todas las áreas'
                          : `🏢 ${filters.areaIds.length} seleccionada${filters.areaIds.length > 1 ? 's' : ''}`
                        }
                      </span>
                      <span>▾</span>
                    </Button>
                    {showAreaDropdown && (
                      <div
                        className="position-absolute bg-white border rounded shadow-sm p-2 z-3"
                        style={{ top: '100%', left: 0, minWidth: '240px', maxHeight: '220px', overflowY: 'auto', zIndex: 1000 }}
                      >
                        <Form.Control
                          size="sm" placeholder="Buscar área..." className="mb-2"
                          value={areaSearch}
                          onChange={e => setAreaSearch(e.target.value)}
                          autoFocus
                        />
                        {areaOptions
                          .filter(a => a.name.toLowerCase().includes(areaSearch.toLowerCase()))
                          .map(area => (
                            <Form.Check
                              key={area.id}
                              type="checkbox"
                              id={`area-${area.id}`}
                              label={area.name}
                              checked={filters.areaIds.includes(area.id)}
                              onChange={() => toggleArea(area.id)}
                              className="mb-1 small"
                            />
                          ))
                        }
                        {areaOptions.filter(a => a.name.toLowerCase().includes(areaSearch.toLowerCase())).length === 0 && (
                          <div className="text-muted small text-center py-1">Sin resultados</div>
                        )}
                      </div>
                    )}
                  </div>
                </Col>

                {/* Centro de Costo - Dropdown con checkboxes */}
                <Col xs={12} sm={6} lg={3}>
                  <Form.Label className="small mb-1 text-muted">Centro de Costo</Form.Label>
                  <div className="position-relative">
                    <Button
                      size="sm"
                      variant={filters.costCenterIds.length > 0 ? 'primary' : 'outline-secondary'}
                      className="w-100 text-start d-flex justify-content-between align-items-center"
                      onClick={() => { setShowCcDropdown(p => !p); setShowAreaDropdown(false); }}
                      style={{ fontSize: '13px' }}
                    >
                      <span>
                        {filters.costCenterIds.length === 0
                          ? '📁 Todos los centros'
                          : `📁 ${filters.costCenterIds.length} seleccionado${filters.costCenterIds.length > 1 ? 's' : ''}`
                        }
                      </span>
                      <span>▾</span>
                    </Button>
                    {showCcDropdown && (
                      <div
                        className="position-absolute bg-white border rounded shadow-sm p-2 z-3"
                        style={{ top: '100%', left: 0, minWidth: '260px', maxHeight: '220px', overflowY: 'auto', zIndex: 1000 }}
                      >
                        <Form.Control
                          size="sm" placeholder="Buscar centro..." className="mb-2"
                          value={ccSearch}
                          onChange={e => setCcSearch(e.target.value)}
                          autoFocus
                        />
                        {costCenterOptions
                          .filter(c => {
                            const label = `${c.code || ''} ${c.name || ''}`.toLowerCase();
                            return label.includes(ccSearch.toLowerCase());
                          })
                          .map(cc => (
                            <Form.Check
                              key={cc.id}
                              type="checkbox"
                              id={`cc-${cc.id}`}
                              label={`${cc.code ? cc.code + ' - ' : ''}${cc.name || ''}`}
                              checked={filters.costCenterIds.includes(cc.id)}
                              onChange={() => toggleCostCenter(cc.id)}
                              className="mb-1 small"
                            />
                          ))
                        }
                        {costCenterOptions.filter(c => `${c.code || ''} ${c.name || ''}`.toLowerCase().includes(ccSearch.toLowerCase())).length === 0 && (
                          <div className="text-muted small text-center py-1">Sin resultados</div>
                        )}
                      </div>
                    )}
                  </div>
                </Col>

                {/* Fecha Desde */}
                <Col xs={6} sm={4} lg={2}>
                  <Form.Label className="small mb-1 text-muted">Fecha desde</Form.Label>
                  <Form.Control
                    type="date" size="sm"
                    value={filters.startDate}
                    onChange={e => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                  />
                </Col>

                {/* Fecha Hasta */}
                <Col xs={6} sm={4} lg={2}>
                  <Form.Label className="small mb-1 text-muted">Fecha hasta</Form.Label>
                  <Form.Control
                    type="date" size="sm"
                    value={filters.endDate}
                    onChange={e => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                  />
                </Col>
              </Row>

              {/* Chips de filtros activos */}
              {hasActiveFilters && (
                <div className="d-flex flex-wrap gap-1 mt-2">
                  {filters.passengerSearch && (
                    <span className="badge bg-primary-subtle text-primary border border-primary-subtle rounded-pill px-2 py-1" style={{ fontSize: '11px' }}>
                      Pasajero: {filters.passengerSearch}
                      <button className="btn-close btn-close-sm ms-1" style={{ fontSize: '8px' }} onClick={() => setFilters(p => ({ ...p, passengerSearch: '' }))} />
                    </span>
                  )}
                  {filters.requesterSearch && (
                    <span className="badge bg-info-subtle text-info border border-info-subtle rounded-pill px-2 py-1" style={{ fontSize: '11px' }}>
                      Solicitante: {filters.requesterSearch}
                      <button className="btn-close btn-close-sm ms-1" style={{ fontSize: '8px' }} onClick={() => setFilters(p => ({ ...p, requesterSearch: '' }))} />
                    </span>
                  )}
                  {filters.areaIds.map(id => {
                    const a = areaOptions.find(x => x.id === id);
                    return a ? (
                      <span key={id} className="badge bg-success-subtle text-success border border-success-subtle rounded-pill px-2 py-1" style={{ fontSize: '11px' }}>
                        Área: {a.name}
                        <button className="btn-close btn-close-sm ms-1" style={{ fontSize: '8px' }} onClick={() => toggleArea(id)} />
                      </span>
                    ) : null;
                  })}
                  {filters.costCenterIds.map(id => {
                    const cc = costCenterOptions.find(x => x.id === id);
                    return cc ? (
                      <span key={id} className="badge bg-warning-subtle text-warning border border-warning-subtle rounded-pill px-2 py-1" style={{ fontSize: '11px' }}>
                        CC: {cc.code ? `${cc.code} - ` : ''}{cc.name}
                        <button className="btn-close btn-close-sm ms-1" style={{ fontSize: '8px' }} onClick={() => toggleCostCenter(id)} />
                      </span>
                    ) : null;
                  })}
                  {filters.startDate && (
                    <span className="badge bg-secondary-subtle text-secondary border border-secondary-subtle rounded-pill px-2 py-1" style={{ fontSize: '11px' }}>
                      Desde: {filters.startDate}
                      <button className="btn-close btn-close-sm ms-1" style={{ fontSize: '8px' }} onClick={() => setFilters(p => ({ ...p, startDate: '' }))} />
                    </span>
                  )}
                  {filters.endDate && (
                    <span className="badge bg-secondary-subtle text-secondary border border-secondary-subtle rounded-pill px-2 py-1" style={{ fontSize: '11px' }}>
                      Hasta: {filters.endDate}
                      <button className="btn-close btn-close-sm ms-1" style={{ fontSize: '8px' }} onClick={() => setFilters(p => ({ ...p, endDate: '' }))} />
                    </span>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      )}

      {error && <Alert variant="danger">{error}</Alert>}

      {trips.length === 0 ? (
          <Card className="text-center py-5 border-0 shadow-sm">
              <Card.Body>
                  <h5 className="text-muted">No tienes viajes registrados</h5>
                  <Link to="/portal/new-trip" className="btn btn-outline-primary mt-3">
                      Solicitar el primero
                  </Link>
              </Card.Body>
          </Card>
      ) : (
          <>
              {/* --- VIAJES ACTIVOS / PROGRAMADOS --- */}
              <Card className="border-0 shadow-sm mb-5">
                  <Card.Header className="bg-white border-bottom py-3">
                      <h5 className="mb-0 text-primary fw-bold">🚗 Viajes Activos y Programados</h5>
                  </Card.Header>
                  {activeTrips.length === 0 ? (
                      <Card.Body className="text-center py-4 text-muted">No hay viajes activos o programados.</Card.Body>
                  ) : (
                      <Table responsive hover className="mb-0 align-middle">
                          <thead className="bg-light">
                              <tr>
                                  <th className="border-0">Reserva</th>
                                  <th className="border-0">Fecha</th>
                                  <th className="border-0">Ruta</th>
                                  <th className="border-0">Vehículo</th>
                                  <th className="border-0">Valor Empresa</th>
                                  {user.role === 'requester' && (
                                      <>
                                          <th className="border-0">Área</th>
                                          <th className="border-0">Centro Costo</th>
                                      </>
                                  )}
                                  <th className="border-0">Estado / GPS</th>
                                  {user.role === 'requester' && <th className="border-0">Pasajero</th>}
                              </tr>
                          </thead>
                          <tbody>
                              {activeTrips.map(trip => (
                                  <tr key={trip.id}>
                                      <td>
                                          <Badge bg="secondary" style={{ fontSize: '12px', fontFamily: 'monospace' }}>#{trip.id}</Badge>
                                      </td>
                                      <td>
                                          <div className="fw-bold">
                                              {new Date(trip.scheduled_time).toLocaleDateString('es-CL')}
                                          </div>
                                          <div className="small text-muted">
                                              {new Date(trip.scheduled_time).toLocaleTimeString('es-CL', {hour: '2-digit', minute:'2-digit'})}
                                          </div>
                                      </td>
                                      <td>
                                          <div className="small text-truncate" style={{ maxWidth: '250px' }} title={trip.pickup_address}>
                                              🟢 {trip.pickup_address}
                                          </div>
                                          <div className="small text-truncate" style={{ maxWidth: '250px' }} title={trip.dropoff_address}>
                                              🔴 {trip.dropoff_address}
                                          </div>
                                      </td>
                                      <td>
                                          {trip.requested_vehicle_type?.name || 'Estándar'}
                                      </td>
                                      <td>
                                          <div className="fw-bold text-slate-700">
                                              ${Math.round(trip.final_price !== null && trip.final_price !== undefined ? trip.final_price : (trip.estimated_price || 0)).toLocaleString('es-CL')}
                                          </div>
                                      </td>
                                      {user.role === 'requester' && (
                                          <>
                                              <td>{trip.area?.name || '---'}</td>
                                              <td>{trip.cost_center ? `${trip.cost_center.code || ''} ${trip.cost_center.name ? `- ${trip.cost_center.name}` : ''}`.trim() || '---' : '---'}</td>
                                          </>
                                      )}
                                      <td>
                                          <div>{getStatusBadge(trip.status)}</div>
                                          {['confirmed', 'en_route', 'arrived', 'on_board'].includes(trip.status) && (
                                              <div className="mt-2">
                                                  <Button 
                                                      variant={trip.status === 'confirmed' ? "outline-secondary" : "outline-primary"}
                                                      size="sm" 
                                                      onClick={() => {
                                                          setSelectedTripId(trip.id);
                                                          setShowTrackingModal(true);
                                                      }}
                                                      style={{ fontSize: '11px', padding: '2px 8px' }}
                                                  >
                                                      {trip.status === 'confirmed' ? "👨‍✈️ Ver Conductor" : "📡 En Vivo"}
                                                  </Button>
                                              </div>
                                          )}
                                          {canEditOrCancelTrip(trip) && (
                                            <div className="mt-2 d-flex gap-1">
                                                <Button 
                                                    variant="outline-primary" 
                                                    size="sm" 
                                                    onClick={() => handleEditTrip(trip)}
                                                    style={{ fontSize: '11px', padding: '2px 8px' }}
                                                >
                                                    ✏️ Editar
                                                </Button>
                                                <Button 
                                                    variant="outline-danger" 
                                                    size="sm" 
                                                    onClick={() => handleShowCancelModal(trip)}
                                                    style={{ fontSize: '11px', padding: '2px 8px' }}
                                                >
                                                    ❌ Cancelar
                                                </Button>
                                            </div>
                                          )}
                                      </td>
                                      {user.role === 'requester' && (
                                          <td>{trip.passenger_name}</td>
                                      )}
                                  </tr>
                              ))}
                          </tbody>
                      </Table>
                  )}
              </Card>

              {/* --- HISTORIAL DE VIAJES / VIAJES ANTIGUOS --- */}
              <h4 className="mt-4 mb-3 text-secondary d-flex align-items-center gap-2">
                  <span>📜 Historial de Viajes</span>
                  <Badge bg="light" text="dark" className="border font-normal fs-6">
                      Completados, cancelados y no-shows
                  </Badge>
              </h4>
              
              {groupedPastTrips.length === 0 ? (
                  <Card className="text-center py-4 border-0 bg-light text-muted">
                      No hay historial de viajes pasados.
                  </Card>
              ) : (
                  groupedPastTrips.map(group => {
                      const isExpanded = !!expandedMonths[group.label];
                      return (
                          <Card key={group.label} className="border-0 shadow-sm mb-4">
                              <Card.Header 
                                  className="bg-light d-flex justify-content-between align-items-center py-3 border-0"
                                  onClick={() => toggleMonth(group.label)}
                                  style={{ cursor: 'pointer', userSelect: 'none' }}
                              >
                                  <span className="fw-bold text-dark h5 mb-0">
                                      {isExpanded ? '▼' : '▶'} 🗓️ {group.label}
                                  </span>
                                  <span className="badge bg-dark fs-6 py-2 px-3 shadow-sm">
                                      Total Empresa: ${Math.round(group.totalSale).toLocaleString('es-CL')}
                                  </span>
                              </Card.Header>
                              {isExpanded && (
                                  <Table responsive hover className="mb-0 align-middle">
                                      <thead className="bg-white border-bottom">
                                          <tr>
                                              <th className="border-0">Reserva</th>
                                              <th className="border-0">Fecha</th>
                                              <th className="border-0">Ruta</th>
                                              <th className="border-0">Vehículo</th>
                                              <th className="border-0">Valor Empresa</th>
                                              {user.role === 'requester' && (
                                                  <>
                                                      <th className="border-0">Área</th>
                                                      <th className="border-0">Centro Costo</th>
                                                  </>
                                              )}
                                              <th className="border-0">Estado</th>
                                              {user.role === 'requester' && <th className="border-0">Pasajero</th>}
                                          </tr>
                                      </thead>
                                      <tbody>
                                          {group.trips.map(trip => (
                                              <tr key={trip.id}>
                                                  <td>
                                                      <Badge bg="secondary" style={{ fontSize: '12px', fontFamily: 'monospace' }}>#{trip.id}</Badge>
                                                  </td>
                                                  <td>
                                                      <div className="fw-bold">
                                                          {new Date(trip.scheduled_time).toLocaleDateString('es-CL')}
                                                      </div>
                                                      <div className="small text-muted">
                                                          {new Date(trip.scheduled_time).toLocaleTimeString('es-CL', {hour: '2-digit', minute:'2-digit'})}
                                                      </div>
                                                  </td>
                                                  <td>
                                                      <div className="small text-truncate" style={{ maxWidth: '250px' }} title={trip.pickup_address}>
                                                          🟢 {trip.pickup_address}
                                                      </div>
                                                      <div className="small text-truncate" style={{ maxWidth: '250px' }} title={trip.dropoff_address}>
                                                          🔴 {trip.dropoff_address}
                                                      </div>
                                                  </td>
                                                  <td>
                                                      {trip.requested_vehicle_type?.name || 'Estándar'}
                                                  </td>
                                                  <td>
                                                      <div className="fw-bold text-slate-700">
                                                          ${Math.round(trip.final_price !== null && trip.final_price !== undefined ? trip.final_price : (trip.estimated_price || 0)).toLocaleString('es-CL')}
                                                      </div>
                                                  </td>
                                                  {user.role === 'requester' && (
                                                      <>
                                                          <td>{trip.area?.name || '---'}</td>
                                                          <td>{trip.cost_center ? `${trip.cost_center.code || ''} ${trip.cost_center.name ? `- ${trip.cost_center.name}` : ''}`.trim() || '---' : '---'}</td>
                                                      </>
                                                  )}
                                                  <td>
                                                      <div>{getStatusBadge(trip.status)}</div>
                                                  </td>
                                                  {user.role === 'requester' && (
                                                      <td>{trip.passenger_name}</td>
                                                  )}
                                              </tr>
                                          ))}
                                      </tbody>
                                  </Table>
                              )}
                          </Card>
                      );
                  })
              )}
          </>
      )}

      {/* Modal de Seguimiento */}
      <LiveTrackingModal
          show={showTrackingModal}
          onHide={() => {
              setShowTrackingModal(false);
              setSelectedTripId(null);
          }}
          tripId={selectedTripId}
      />

      {/* Modal de Confirmación de Cancelación */}
      <Modal show={showCancelModal} onHide={handleCloseCancelModal} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="text-danger fw-bold h5 d-flex align-items-center gap-2">
            <span>⚠️ Confirmar Cancelación</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          {tripToCancel && (
            <div>
              ¿Estás seguro de que deseas cancelar el viaje <strong>#{tripToCancel.id}</strong> programado para el <strong>{new Date(tripToCancel.scheduled_time).toLocaleDateString('es-CL')}</strong> a las <strong>{new Date(tripToCancel.scheduled_time).toLocaleTimeString('es-CL', {hour: '2-digit', minute:'2-digit'})}</strong>?
              <br/><br/>
              <span className="text-muted small">Esta acción no se puede deshacer y el viaje cambiará a estado <strong>Cancelado</strong>.</span>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" onClick={handleCloseCancelModal} disabled={cancelLoading}>
            Atrás
          </Button>
          <Button variant="danger" onClick={handleConfirmCancel} disabled={cancelLoading}>
            {cancelLoading ? <Spinner animation="border" size="sm" /> : "Confirmar Cancelar"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MyTripsPage;
